import React, { useEffect, useState } from "react";
import API from "../api";
import { Card, Badge, Toast, Loading, Empty, SectionHeader } from "../components/UI";

export default function Handoffs() {
  const [handoffs, setHandoffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    API.get("/handoffs")
      .then(r => setHandoffs(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const resolve = async (id) => {
    try {
      await API.put(`/handoff/${id}/resolve`);
      setHandoffs(p => p.map(h => h._id === id ? { ...h, status: "resolved" } : h));
      setToast({ message: "Handoff resolved!", type: "success" });
    } catch { setToast({ message: "Failed", type: "error" }); }
  };

  const deleteHandoff = async (id) => {
    if (!window.confirm("Delete?")) return;
    try {
      await API.delete(`/handoff/${id}`);
      setHandoffs(p => p.filter(h => h._id !== id));
      setToast({ message: "Deleted", type: "success" });
    } catch { setToast({ message: "Failed", type: "error" }); }
  };

  const pending = handoffs.filter(h => h.status === "pending");
  const resolved = handoffs.filter(h => h.status === "resolved");

  if (loading) return <Loading />;

  const th = { textAlign: "left", padding: "12px 16px", fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", background: "var(--bg-elevated)" };
  const td = { padding: "12px 16px", fontSize: 13, verticalAlign: "middle" };

  return (
    <div className="fade-in">
      <SectionHeader
        title="Human Handoffs"
        subtitle={`${pending.length} pending — ${resolved.length} resolved`}
      />

      {/* Pending */}
      {pending.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--accent-red)", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "var(--font-mono)", marginBottom: 10 }}>
            🔴 Pending — Action Required
          </div>
          <Card style={{ padding: 0, overflow: "hidden", marginBottom: 20 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  {["Phone", "Name", "Reason", "Client", "Time", "Actions"].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pending.map((h, i) => (
                  <tr key={h._id} style={{ borderBottom: "1px solid var(--border)", background: i % 2 === 0 ? "rgba(255,77,77,0.03)" : "transparent" }}>
                    <td style={{ ...td, fontFamily: "var(--font-mono)", fontSize: 12 }}>
                      <a href={`https://wa.me/${h.phone}`} target="_blank" rel="noreferrer"
                        style={{ color: "var(--accent)", textDecoration: "none" }}>
                        {h.phone} 📱
                      </a>
                    </td>
                    <td style={{ ...td, color: h.name ? "var(--text-primary)" : "var(--text-muted)" }}>
                      {h.name || "—"}
                    </td>
                    <td style={{ ...td, color: "var(--text-secondary)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {h.reason}
                    </td>
                    <td style={{ ...td, color: "var(--text-secondary)" }}>
                      {h.clientId?.name || "—"}
                    </td>
                    <td style={{ ...td, fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                      {new Date(h.createdAt).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td style={td}>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => resolve(h._id)}
                          style={{ background: "rgba(37,211,102,0.1)", border: "1px solid rgba(37,211,102,0.3)", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 12, color: "var(--accent)", fontFamily: "var(--font-body)" }}>
                          ✓ Resolve
                        </button>
                        <button onClick={() => deleteHandoff(h._id)}
                          style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 6, width: 28, height: 28, cursor: "pointer", fontSize: 12, color: "var(--accent-red)" }}>
                          ✕
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {/* Resolved */}
      {resolved.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "var(--font-mono)", marginBottom: 10 }}>
            ✅ Resolved
          </div>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  {["Phone", "Name", "Reason", "Client", "Time", ""].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {resolved.map((h, i) => (
                  <tr key={h._id} style={{ borderBottom: "1px solid var(--border)", opacity: 0.6 }}>
                    <td style={{ ...td, fontFamily: "var(--font-mono)", fontSize: 12 }}>{h.phone}</td>
                    <td style={{ ...td, color: "var(--text-muted)" }}>{h.name || "—"}</td>
                    <td style={{ ...td, color: "var(--text-muted)", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.reason}</td>
                    <td style={{ ...td, color: "var(--text-muted)" }}>{h.clientId?.name || "—"}</td>
                    <td style={{ ...td, fontSize: 11, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                      {new Date(h.createdAt).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td style={td}>
                      <button onClick={() => deleteHandoff(h._id)}
                        style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 6, width: 28, height: 28, cursor: "pointer", fontSize: 12, color: "var(--accent-red)" }}>
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {handoffs.length === 0 && (
        <Card><Empty message="Koi handoff nahi — sab theek hai! 🎉" icon="✅" /></Card>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
